/** Общий SVG-рендер швов пола: геометрия совпадает с 3D CanvasTexture. */
import {
	floorPatternCycle,
	floorPatternSegments,
	FloorMaterial,
	isWholeFloorImage,
	type FloorLook,
} from "@/shared/catalog";
import type { Room } from "@/widgets/plan";

interface Props {
	room: Room;
	look: FloorLook;
}

const boundsOf = (room: Room) =>
	room.inner.reduce(
		(bounds, point) => ({
			minX: Math.min(bounds.minX, point.x),
			maxX: Math.max(bounds.maxX, point.x),
			minY: Math.min(bounds.minY, point.y),
			maxY: Math.max(bounds.maxY, point.y),
		}),
		{ minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
	);

/** Остаток от деления, всегда неотрицательный: -100 по модулю 600 — это 500. */
const wrap = (value: number, period: number) =>
	period > 0 ? ((value % period) + period) % period : value;

export const FloorPattern2D = ({ room, look }: Props) => {
	const bounds = boundsOf(room);
	const patternId = `room-floor-${room.id}-${look.layout}-${Math.round(look.rotation)}`;
	// Паркет и линолеум картинкой — рисунок без швов укладки: на чертеже просто его цвет
	const cycle = floorPatternCycle(look);
	const padding = Math.max(cycle.width, cycle.height);
	const centerX = (bounds.minX + bounds.maxX) / 2;
	const centerY = (bounds.minY + bounds.maxY) / 2;
	// Раскладку можно увести сколько угодно далеко: узор повторяется через период, поэтому
	// рисуем его со сдвигом по модулю периода — рисунок тот же, а комната всегда покрыта целиком
	const offsetX = wrap(look.offsetX, cycle.width);
	const offsetY = wrap(look.offsetY, cycle.height);
	// Повёрнутый узор должен накрыть и углы комнаты: расширяем область на полдиагонали —
	// целым числом периодов, чтобы начало узора сдвинулось ровно на период и рисунок не поменялся
	const halfDiagonal =
		look.rotation % 90 === 0
			? 0
			: Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY) / 2;
	const extraX = cycle.width > 0 ? Math.ceil(halfDiagonal / cycle.width) * cycle.width : 0;
	const extraY = cycle.height > 0 ? Math.ceil(halfDiagonal / cycle.height) * cycle.height : 0;
	const area = {
		minX: bounds.minX - extraX,
		maxX: bounds.maxX + extraX,
		minY: bounds.minY - extraY,
		maxY: bounds.maxY + extraY,
	};
	const segments = isWholeFloorImage(look.texture)
		? []
		: floorPatternSegments(area, look, padding).map((segment) => ({
				...segment,
				fromX: segment.fromX + offsetX,
				fromY: segment.fromY + offsetY,
				toX: segment.toX + offsetX,
				toY: segment.toY + offsetY,
			}));
	const strokeWidth = Math.max(1, look.gap * (look.material === FloorMaterial.Tile ? 0.9 : 1 / 3));

	return (
		<g style={{ pointerEvents: "auto" }}>
			<defs>
				<clipPath id={patternId}>
					<polygon points={room.inner.map((point) => `${point.x},${point.y}`).join(" ")} />
				</clipPath>
			</defs>
			<polygon
				points={room.inner.map((point) => `${point.x},${point.y}`).join(" ")}
				fill={look.color}
				stroke="rgba(0,0,0,0.04)"
				strokeWidth={1}
			/>
			<g clipPath={`url(#${patternId})`} style={{ pointerEvents: "none" }}>
				<g transform={`rotate(${look.rotation} ${centerX} ${centerY})`}>
					{segments.map((segment, index) => (
						<path
							key={`${patternId}-${index}`}
							d={`M ${segment.fromX} ${segment.fromY} L ${segment.toX} ${segment.toY}`}
							stroke={look.seamColor}
							strokeWidth={strokeWidth}
							fill="none"
						/>
					))}
				</g>
			</g>
		</g>
	);
};
